import { gql, useMutation } from '@apollo/client';
import { useGraphql } from 'utils/hooks/useGraphql';
import { SmsSmartOptInLink } from '../modals/SmsSmartOptInModal/types';

const SMART_OPT_IN = gql`
  fragment smartOptInAttributes on SmartOptIn {
    id
    name
    description
    brandName
    messageTypeId
    smsSenderId
    termsLink
    privacyLink
    fallbackUrl
    optInUrl
    createdAt
  }
`;

export const fetchOptInLinksByProjectIdQuery = gql`
  query FetchOptInLinksByProjectId {
    getSmartOptInsByProjectId {
      ...smartOptInAttributes
    }
  }
  ${SMART_OPT_IN}
`;

const createSmartOptInLinkMutation = gql`
  mutation CreateSmartOptInLink(
    $name: String!
    $description: String
    $brandName: String!
    $messageTypeId: Long!
    $smsSenderId: Long!
    $termsLink: String
    $privacyLink: String
    $fallbackUrl: String!
  ) {
    createSmartOptIn(
      name: $name
      description: $description
      brandName: $brandName
      messageTypeId: $messageTypeId
      smsSenderId: $smsSenderId
      termsLink: $termsLink
      privacyLink: $privacyLink
      fallbackUrl: $fallbackUrl
    ) {
      success {
        ...smartOptInAttributes
      }
      failure {
        message
      }
    }
  }
  ${SMART_OPT_IN}
`;

const updateSmartOptInLinkMutation = gql`
  mutation UpdateSmartOptInLink($id: Long!, $fields: SmartOptInFieldsInput!) {
    updateSmartOptIn(id: $id, fields: $fields) {
      success {
        ...smartOptInAttributes
      }
      failure {
        message
      }
    }
  }
  ${SMART_OPT_IN}
`;

export const useSmartOptInLinksByProjectId = () => {
  const { isLoading, data, errorMessage, refetch } = useGraphql(fetchOptInLinksByProjectIdQuery);

  return {
    isLoading,
    data: data?.getSmartOptInsByProjectId,
    errorMessage: errorMessage?.message,
    refetch,
  };
};

export const useCreateSmartOptInLink = (
  onComplete: (contactCard: SmsSmartOptInLink) => void,
  onError: (message: string) => void,
) => {
  const [createSmartOptIn] = useMutation(createSmartOptInLinkMutation, {
    onCompleted: (response) => {
      const { success, failure } = response.createSmartOptIn;
      if (success) {
        onComplete(success);
      }
      if (failure) {
        onError(failure.message);
      }
    },
    onError: (e) => onError(e.message),
    refetchQueries: [{ query: fetchOptInLinksByProjectIdQuery }, 'FetchOptInLinksByProjectId'],
  });

  return createSmartOptIn;
};

export const useUpdateSmartOptInLink = (
  onComplete: (contactCard: SmsSmartOptInLink) => void,
  onError: (message: string) => void,
) => {
  const [updateSmartOptIn] = useMutation(updateSmartOptInLinkMutation, {
    onCompleted: (response) => {
      const { success, failure } = response.updateSmartOptIn;
      if (success) {
        onComplete(success);
      }
      if (failure) {
        onError(failure.message);
      }
    },
    onError: (e) => onError(e.message),
  });

  return updateSmartOptIn;
};
